<template>
  <Dialog
    v-model:visible="isVisible"
    :header="header"
    :modal="true"
    :closable="true"
    :style="dialogStyle"
  >
    <template #header>
      <div class="inline-flex items-center justify-center gap-2">
        <strong>{{ header }}</strong>
      </div>
    </template>
    <div class="p-dialog-content">
      <!-- Slot to pass dynamic content into the dialog -->
      <slot />
    </div>
    <template #footer>
      <slot name="footer" />
    </template>
  </Dialog>
</template>

<script setup>
import { defineProps, defineEmits, watch, computed } from 'vue';
import { useDialog } from '@/composables/useDialog';
import { projectStore } from '@/components/project/officeProject/projectContents/projectStore';

const props = defineProps({
    modelValue: Boolean,  // To control visibility from the parent
    header: {
        type: String,
        default: 'Dialog'
    },
    width: {
        type: String,
        default: null
    },
    fullscreen: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:modelValue']);

const dialogStyle = computed(() => {
    if (props.fullscreen) {
        return {
            width: '95vw',
            height: '95vh',
            maxWidth: '95vw',
            maxHeight: '95vh'
        };
    }
    
    return {
        width: props.width || projectStore.modalWidth || '90vw',
        maxWidth: '1400px'
    };
});

// Use the dialog composable
const { isVisible } = useDialog(props.modelValue);

// Watch for changes in `modelValue` prop and update the composable state
watch(() => props.modelValue, (newVal) => {
    isVisible.value = newVal;
});

// Emit updates when the composable state changes
watch(isVisible, (newVal) => {
    emit('update:modelValue', newVal);
});
</script>

<style scoped>
.p-dialog-content {
    padding: 1.5rem;
    width: 100%;
    max-width: 100%;
}

:deep(.p-dialog) {
    max-width: 100%;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}

:deep(.p-dialog-content) {
    flex: 1;
    overflow: auto;
    width: 100%;
}
</style>